import React, { useCallback, useState } from 'react';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import { Center, Loader, Stack } from '@mantine/core';

import { When } from '../common';
import {
    GET_PRICE_SYNC_QUEUE_STATS,
    GET_PRICE_SYNC_QUEUE_ROWS,
    GET_PRICE_SYNC_QUEUE_ROW_DETAIL,
} from '../../apollo/queries/priceSync';
import {
    TRIGGER_PRICE_SYNC_NOW,
    RETRY_PRICE_SYNC_QUEUE_ROW,
} from '../../apollo/mutations/priceSync';
import {
    showErrorNotification,
    showSuccessNotification,
} from '../../notifications';
import { PriceSyncMonitorHeader } from './PriceSyncMonitorHeader';
import { PriceSyncStatsGrid } from './PriceSyncStatsGrid';
import { PriceSyncQueueTable } from './PriceSyncQueueTable';
import type { PriceSyncQueueStats, QueueRow } from './priceSync.types';
import { PAGE_SIZE } from './priceSync.utils';

export function PriceSyncMonitor() {
    const client = useApolloClient();
    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState('');
    const [storeFilter, setStoreFilter] = useState('');
    const [skuFilter, setSkuFilter] = useState('');
    const [expandedRow, setExpandedRow] = useState<string | null>(null);

    const {
        data: statsData,
        loading: statsLoading,
        refetch: refetchStats,
    } = useQuery(GET_PRICE_SYNC_QUEUE_STATS, {
        fetchPolicy: 'network-only',
        pollInterval: 30_000,
    });

    const {
        data: rowsData,
        loading: rowsLoading,
        refetch: refetchRows,
    } = useQuery(GET_PRICE_SYNC_QUEUE_ROWS, {
        fetchPolicy: 'network-only',
        pollInterval: 30_000,
        variables: {
            status: statusFilter || null,
            storeNo: storeFilter || null,
            sku: skuFilter || null,
            take: PAGE_SIZE,
            skip: (page - 1) * PAGE_SIZE,
        },
    });

    const refetchMonitorData = useCallback(() => {
        refetchStats();
        refetchRows();
        client
            .refetchQueries({
                include: [GET_PRICE_SYNC_QUEUE_ROW_DETAIL],
            })
            .catch(() => undefined);
    }, [client, refetchStats, refetchRows]);

    const [triggerSync, { loading: triggerLoading }] = useMutation(
        TRIGGER_PRICE_SYNC_NOW,
        {
            onCompleted: result => {
                showSuccessNotification(
                    `Sync dispatched — ${result.triggerPriceSyncNow.processed} product(s) processed.`,
                );
                refetchMonitorData();
            },
            onError: err => showErrorNotification(err.message),
        },
    );

    const [retryRow, { loading: retryLoading }] = useMutation(
        RETRY_PRICE_SYNC_QUEUE_ROW,
        {
            onCompleted: () => {
                showSuccessNotification('Row reset to Pending.');
                refetchMonitorData();
            },
            onError: err => showErrorNotification(err.message),
        },
    );

    const stats: PriceSyncQueueStats | undefined =
        statsData?.getPriceSyncQueueStats;
    const rows: QueueRow[] = rowsData?.getPriceSyncQueueRows?.rows ?? [];
    const total: number = rowsData?.getPriceSyncQueueRows?.total ?? 0;

    const handleRefresh = useCallback(() => {
        refetchMonitorData();
    }, [refetchMonitorData]);

    const handleRetry = useCallback(
        (queueId: string) => {
            retryRow({ variables: { queueId } });
        },
        [retryRow],
    );

    const handleStatusChange = useCallback((value: string) => {
        setStatusFilter(value);
        setPage(1);
    }, []);

    const handleStoreChange = useCallback((value: string) => {
        setStoreFilter(value);
        setPage(1);
    }, []);

    const handleSkuChange = useCallback((value: string) => {
        setSkuFilter(value);
        setPage(1);
    }, []);

    return (
        <Stack gap="xl">
            <PriceSyncMonitorHeader
                stats={stats}
                refreshLoading={statsLoading || rowsLoading}
                triggerLoading={triggerLoading}
                onRefresh={handleRefresh}
                onTriggerSync={() => triggerSync()}
            />

            <When
                condition={!(statsLoading && !stats)}
                fallback={
                    <Center>
                        <Loader />
                    </Center>
                }
            >
                {stats !== undefined ? (
                    <PriceSyncStatsGrid stats={stats} />
                ) : null}
            </When>

            <PriceSyncQueueTable
                rows={rows}
                total={total}
                page={page}
                rowsLoading={rowsLoading}
                retryLoading={retryLoading}
                expandedRow={expandedRow}
                statusFilter={statusFilter}
                storeFilter={storeFilter}
                skuFilter={skuFilter}
                onPageChange={setPage}
                onExpandedRowChange={setExpandedRow}
                onRetry={handleRetry}
                onStatusChange={handleStatusChange}
                onStoreChange={handleStoreChange}
                onSkuChange={handleSkuChange}
            />
        </Stack>
    );
}
